/**
 * Page-view tracking registry for Cohesion events.
 *
 * Prevents duplicate analytics events within a single "page view" in our SPA.
 */

import { debugCohesion } from '../../utils/debug-logger';

// #region State

/** @type {Set<string>} Keys of events already tracked this page view */
const pageViewTrackedKeys = new Set();

// #endregion

// #region Public API

/**
 * Reset all tracked keys for a new page view.
 * Call this on navigation once you know the user is seeing a *new* page.
 */
export function resetPageViewTracking() {
  const previousCount = pageViewTrackedKeys.size;
  pageViewTrackedKeys.clear();

  debugCohesion(() =>
    previousCount > 0
      ? console.log(
          `🔄 [Cohesion] Reset page view tracking for new page: cleared ${previousCount} keys`,
        )
      : undefined,
  );
}

/**
 * Check if an event should be tracked and mark it as tracked.
 *
 * @param {string} eventName - The event name (e.g., 'ElementViewed')
 * @param {object} params - The event params
 * @param {((params: object) => string) | undefined} keyBuilder - Optional function to build unique key from params
 * @returns {boolean} True if event should be tracked, false if already tracked this page view
 *
 * @example No keyBuilder (one event per page)
 * shouldTrackOncePerPageView('AccountLogin', params, undefined)
 * // Key: 'AccountLogin' — only one login event per page
 *
 * @example With keyBuilder (multiple unique events per page)
 * shouldTrackOncePerPageView('ElementViewed', params, (p) => `${p.webElement.name}:${p.webElement.location}`)
 * // Key: 'ElementViewed:product-card:hero' — one per unique element
 */
export function shouldTrackOncePerPageView(eventName, params, keyBuilder) {
  const key = `${eventName}${keyBuilder ? `:${keyBuilder(params)}` : ''}`;

  if (pageViewTrackedKeys.has(key)) {
    debugCohesion(() =>
      console.warn(
        `⚠️ [${eventName}] Already tracked "${key}" this page. Skipping duplicate.`,
      ),
    );
    return false;
  }

  pageViewTrackedKeys.add(key);

  return true;
}

/**
 * Get all currently tracked keys.
 * Useful for debugging.
 *
 * @returns {string[]} Array of all tracked keys
 */
export function getPageViewTrackedKeys() {
  return Array.from(pageViewTrackedKeys);
}

// #endregion

// #region Browser Debug Tooling (dev only)

const isDev = process.env.NODE_ENV !== 'production';

if (isDev && typeof window !== 'undefined') {
  window.__cohesionDebug = {
    ...(window.__cohesionDebug || {}),

    pageViewTrackedKeys: () => {
      const keys = getPageViewTrackedKeys();
      console.table(
        keys.map((key) => {
          const [eventName, ...rest] = key.split(':');
          return {
            eventName,
            uniqueKey: rest.join(':') || '(none - one per page)',
            fullKey: key,
          };
        }),
      );
      return keys;
    },

    resetPageViewTracking,

    pageViewTrackingStats: () => {
      const keys = getPageViewTrackedKeys();
      /** @type {Record<string, number>} */
      const byEventType = {};

      keys.forEach((key) => {
        const eventName = key.split(':')[0];
        byEventType[eventName] = (byEventType[eventName] || 0) + 1;
      });

      const stats = { totalTracked: keys.length, byEventType };
      console.log('📊 Cohesion Page-View Tracking Stats:', stats);
      return stats;
    },
  };
}

// #endregion
